/**
 * Video Autoplay on Scroll
 *
 * This module handles intelligent loading and autoplay of videos in product cards:
 * - Videos start loading when scrolling NEAR them (500px below viewport)
 * - Videos play when scrolling INTO view (50% visible)
 * - Videos pause when scrolling OUT of view
 */

(function() {
  // Check if Intersection Observer is supported
  if (!('IntersectionObserver' in window)) {
    return;
  }

  // Configuration for load observer - start loading videos when near
  const loadObserverOptions = {
    rootMargin: '500px 0px' // Start loading 500px before video enters viewport
  };

  // Configuration for play observer - play when video is visible
  const playObserverOptions = {
    threshold: 0.5 // Play when 50% of the video is visible
  };

  // Create intersection observer for video loading
  const loadObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting && !video.hasAttribute('data-video-loaded')) {
        // Start loading the video (preload="none" prevents initial load)
        video.load();
        video.setAttribute('data-video-loaded', 'true');
      }
    });
  }, loadObserverOptions);

  // Create intersection observer for video playback
  const playObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;

      if (entry.isIntersecting) {
        // Video is in view, attempt to play
        const playPromise = video.play();

        // Handle play promise to prevent console errors
        if (playPromise !== undefined) {
          playPromise
            .catch((error) => {
              // Autoplay was prevented or other playback error
              // This is usually due to browser autoplay policies
              console.debug('Video autoplay prevented:', error);
            });
        }
      } else {
        // Video is out of view, pause it
        video.pause();
      }
    });
  }, playObserverOptions);

  // Function to initialize video observers
  function initializeVideoObservers() {
    const videos = document.querySelectorAll('[data-video-card]');
    videos.forEach((video) => {
      // Observe with both load and play observers
      loadObserver.observe(video);
      playObserver.observe(video);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeVideoObservers);
  } else {
    initializeVideoObservers();
  }

  // Handle dynamically added videos (e.g., from infinite scroll or AJAX)
  // Watch for new videos being added to the DOM
  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        // Check if any new videos were added
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node or its children contain videos
            if (node.hasAttribute && node.hasAttribute('data-video-card')) {
              loadObserver.observe(node);
              playObserver.observe(node);
            }

            // Check children
            const childVideos = node.querySelectorAll && node.querySelectorAll('[data-video-card]');
            if (childVideos) {
              childVideos.forEach((video) => {
                loadObserver.observe(video);
                playObserver.observe(video);
              });
            }
          }
        });
      }
    });
  });

  // Only observe if we want to support dynamic content
  // Uncomment if needed for infinite scroll or AJAX loading
  // mutationObserver.observe(document.body, {
  //   childList: true,
  //   subtree: true
  // });
})();
